import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {
  CentralTheme,
  ThemeService,
  ThemeToggleComponent,
} from '@frontend/theme';
import {
  CentralDarkLogoPNG,
  CentralSVG,
  CentralWhiteLogoPNG,
} from '@shared-assets/images';
import { SvgComponent } from '@terminus-ui/svg';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBars,
  faBell,
  faBriefcase,
  faCalendar,
  faChartLine,
  faChartSimpleHorizontal,
  faCheckCircle,
  faChevronDown,
  faChevronLeft,
  faChevronsDown,
  faChevronUp,
  faCommentsDollar,
  faEnvelope,
  faFileText,
  faFlag,
  faGalaxy,
  faLightbulbGear,
  faLock,
  faMagic,
  faPalette,
  faPeople,
  faPhoneLaptop,
  faReceipt,
  faSpaceStationMoonConstruction,
  faTable,
  faTachometer,
  faUserFriends,
  faUsersRectangle,
} from '@fortawesome/pro-light-svg-icons';
import { faComment, faGear, faUser } from '@fortawesome/pro-regular-svg-icons';
import { NavItemService } from '@frontend/util/nav-item-access';
import { UserInfo } from '@terminus-ui/nav';
import { ApplicationManagerApiService } from 'application-manager';
import { AuthService } from 'auth';
import { EntitlementsService } from 'entitlements';
import { GlobalPanelModule } from 'global-panel';
import { LakeFrontV2Module } from 'lake-front-v2';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { PrimeNG } from 'primeng/config';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { combineLatest, map, Observable, of, take } from 'rxjs';

const demandscienceLogoWhite = require('../images/demandscience-logo-white2.svg');

@Component({
  selector: 'ds-central-main-layout',
  templateUrl: './central-main-layout.component.html',
  styleUrls: ['./central-main-layout.component.scss'],
  providers: [],
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    SvgComponent,
    FontAwesomeModule,
    MenuModule,
    GlobalPanelModule,
    LakeFrontV2Module,
    ThemeToggleComponent,
    DialogModule,
    FormsModule,
    CheckboxModule,
  ],
})
export class CentralMainLayoutComponent implements OnInit {
  private primeNGConfig = inject(PrimeNG);
  private themeService = inject(ThemeService);

  public centralLogo = CentralSVG;
  public centralWhiteLogo = CentralWhiteLogoPNG;
  public centralDarkLogo = CentralDarkLogoPNG;
  public faBars = faBars;
  public faFlag = faFlag;
  public faTable = faTable;
  public faGear = faGear;
  public faGalaxy = faGalaxy;
  public faMagic = faMagic;
  public faChartLine = faChartLine;
  public faEnvelope = faEnvelope;
  public faTachometer = faTachometer;
  public faPhoneLaptop = faPhoneLaptop;
  public faComment = faComment;
  public faBell = faBell;
  public faUser = faUser;
  public faChartSimpleHorizontal = faChartSimpleHorizontal;
  public faChevronDown = faChevronDown;
  public faLightbulbGear = faLightbulbGear;
  public faPalette = faPalette;
  public faCheckCircle = faCheckCircle;
  public faPeople = faPeople;
  public faUserFriends = faUserFriends;
  public faBriefcase = faBriefcase;
  public faCalendar = faCalendar;
  public demandScienceLogoWhite = demandscienceLogoWhite;
  public faUsersRectangle = faUsersRectangle;
  public faChevronUp = faChevronUp;
  public faChevronsDown = faChevronsDown;
  public faSpaceStationMoonConstruction = faSpaceStationMoonConstruction;
  public faCommentsDollar = faCommentsDollar;
  public faLock = faLock;
  public faFileText = faFileText;
  public faReceipt = faReceipt;
  public faChevronLeft = faChevronLeft;
  private authService = inject(AuthService);
  private entitlementService = inject(EntitlementsService);
  private navItemsService = inject(NavItemService);
  private router = inject(Router);
  private applicationManagerApi = inject(ApplicationManagerApiService);
  menuItems = [
    {
      label: 'Financial Events',
      link: '/central/data-unification/financial-events',
    },
    { label: 'News Events', link: '/central/data-unification/news-events' },
    { label: 'Job Openings', link: '/central/data-unification/job-openings' },
    { label: 'Companies', link: '/central/data-unification/companies' },
    { label: 'Contacts LF 2.5', link: '/central/data-unification/contact-2-5' },
    {
      label: 'Contacts + HEMS',
      link: '/central/data-unification/contacts-hems',
    },
    { label: 'Technologies', link: '/central/data-unification/technologies' },
    {
      label: 'Technology Detection',
      link: '/central/data-unification/technology-detection',
    },
    { label: 'Data Overview', link: '/central/data-unification/data-overview' },
  ];

  formattedOrgName$ = this.authService.hubUserData$.pipe(
    map((hubOrgData) => hubOrgData?.orgName.split(' ')[0]),
  );

  user$: Observable<UserInfo | null> = this.navItemsService.user$;

  showDataUniMenu = false;
  showEULA$: Observable<boolean> = of(false);
  eulaCheckbox = false;
  showNoAccess: boolean;
  currentTheme: string = this.themeService.getCurrentTheme();
  dataUniItems = [
    { label: 'Financial Events' },
    { label: 'Job Openings' },
    { label: 'News Events ' },
  ];

  isAdmin$ = this.entitlementService
    .getEntitlement$({ requireMyTAdmin: true })
    .pipe(map((isAdmin) => isAdmin === true));

  hasCentralAccess$ = this.entitlementService.getEntitlement$({
    requireHubReportsAccess: 'app.central',
  });

  hasDemandAccess$ = this.entitlementService.getEntitlement$({
    requireHubReportsAccess: 'app.central.demand',
  });

  hasAdvertisingAccess$ = this.entitlementService.getEntitlement$({
    requireHubReportsAccess: 'app.central.advertising',
  });

  hasIntentAccess$ = this.entitlementService.getEntitlement$({
    requireHubReportsAccess: 'app.central.intent',
  });

  NavButtonTheme = {
    color: '{surface-100}',
  };

  profileItems = [
    {
      label: 'Help',
      icon: 'pi pi-book',
      url: 'https://support.demandscience.com/hc/en-us/categories/41046023276435-Central',
    },
    {
      label: 'Contact Support',
      icon: 'pi pi-question',
      url: 'https://support.demandscience.com/hc/en-us/categories/41046023276435-Central',
    },
    { label: 'Return Home', icon: 'pi pi-home', routerLink: '/home' },
    { label: 'Logout', icon: 'pi pi-sign-out', routerLink: '/logout' },
  ];

  ngOnInit() {
    this.primeNGConfig.setConfig({
      theme: {
        preset: CentralTheme,
        options: {
          prefix: '',
          darkModeSelector: '.central-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    });

    // Initialize theme service - it will handle the theme application
    this.themeService.listenToSystemThemeChanges();

    this.injectScripts();
    this.getPolicies();
    this.redirectIfNoPath();
  }

  injectScripts() {
    const warmlyScript = document.createElement('script');
    warmlyScript.id = 'warmly-script-loader';
    warmlyScript.src =
      'https://opps-widget.getwarmly.com/warmly.js?clientId=ca6c8936d92397333971855a51b363ec';
    warmlyScript.defer = true;
    const random = Math.random().toString(36).substring(7);
    const apolloScript = document.createElement('script');
    apolloScript.src =
      'https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache=' +
      random;
    apolloScript.async = true;
    apolloScript.onload = function () {
      // @ts-ignore
      window.trackingFunctions?.onLoad({ appId: '6633937b67f3b60439035d6e' });
    };
    document.head.appendChild(warmlyScript);
    document.head.appendChild(apolloScript);
  }

  redirectIfNoPath() {
    const urlTree = this.router.parseUrl(this.router.url);

    // check if the current path is exactly '/central' without any additional segments
    if (
      urlTree.root.children['primary']?.segments
        .map((s) => s.path)
        .join('/') === 'central'
    ) {
      combineLatest([
        this.hasCentralAccess$,
        this.hasDemandAccess$,
        this.hasAdvertisingAccess$,
        this.hasIntentAccess$,
      ])
        .pipe(take(1))
        .subscribe(([hasCentral, hasDemand, hasAdvertising, hasIntent]) => {
          if (!hasCentral) {
            this.showNoAccess = true;
          } else if (hasDemand) {
            this.router.navigate(['/central/demand']);
          } else if (hasAdvertising) {
            this.router.navigate(['/central/advertising']);
          } else if (hasIntent) {
            this.router.navigate(['/central/intent']);
            // something is really wrong if we get here
          } else {
            this.showNoAccess = true;
          }
        });
    }
  }

  getPolicies() {
    this.showEULA$ = this.applicationManagerApi.listPolicies().pipe(
      map((resp) => {
        const eulaAcknowledged = resp.policies?.find(
          (policy) =>
            policy.policyId === 'CENTRAL_EULA_v1' &&
            policy.name === 'Central EULA v1',
        );
        return eulaAcknowledged ? false : true;
      }),
    );
  }

  acceptEULA() {
    this.applicationManagerApi
      .upsertPolicy('CENTRAL_EULA_v1', 'Central EULA v1')
      .pipe(take(1))
      .subscribe({
        next: () => {
          console.debug('EULA accepted');
          // refresh the policies to update the dialog state
          this.getPolicies();
        },
        error: (err) => {
          console.error('Error accepting EULA', err);
        },
      });
  }

  declineEULA() {
    this.router.navigate(['/login']);
  }

  onThemeChange(event: 'light' | 'dark') {
    this.currentTheme = this.themeService.getCurrentTheme();
  }
}

@Component({
  selector: 'ds-central-dashboard',
  template: `<div class="flex flex-column gap-3 p-7 w-full main-content">
    <h1>Dashboard</h1>
    <span> Welcome to Demand Science Central</span>
  </div>`,
})
export class CentralDashboardComponent {}
